import { defineStore } from 'pinia'
import makeRequest from '../services/makeRequest'

// Define interfaces for authentication
export interface RegisterPayload {
  username: string
  password: string
  password2: string
  email: string
  first_name: string
  last_name: string
  phone_number?: string
}

export interface LoginPayload {
  username: string
  password: string
}

export interface ActivateAccountPayload {
  email: string
  otp: string
}

export interface UserProfile {
  id: number
  username: string
  email: string
  first_name: string
  last_name: string
  bio: string | null
  profile_picture: string | null
  phone_number: string | null
  is_phone_verified: boolean | null
  permissions: string[]
  roles: Array<{
    id: number
    name: string
  }>
}

export interface UpdateProfilePayload {
  first_name?: string
  last_name?: string
  bio?: string
  phone_number?: string
  profile_picture?: string
}

export interface ResetPasswordPayload {
  email: string
}

export interface ConfirmResetPasswordPayload {
  token: string
  new_password: string
  confirm_password: string
  email: string
}

export interface DeactivateAccountPayload {
  email: string
}

export interface ResendOtpPayload {
  email: string
}

export interface ReactivateAccountPayload {
  email: string
}

export interface LogoutPayload {
  refresh: string
}

export interface AuthTokens {
  access: string
  refresh: string
}

export interface AuthUser {
  id: number
  username: string
  email: string
}

export const useAuthStore = defineStore('auth-store', {
  state: () => {
    return {
      user: null as AuthUser | null,
      userProfile: null as UserProfile | null,
      accessToken: null as string | null,
      refreshToken: null as string | null,
      isAuthenticated: false,
      loading: false,
      submitting: false,
      profileLoading: false,
      // Legacy fields for backward compatibility
      userName: '',
      email: '',
      memberSince: '8/12/2020',
      pfp: 'https://picsum.photos/id/22/200/300',
      is2FAEnabled: false,
      logingin: false,
    }
  },

  getters: {
    hasPermission: (state) => (permission: string) => {
      return state.userProfile?.permissions.includes(permission) || false
    },
    hasRole: (state) => (roleName: string) => {
      return state.userProfile?.roles.some((role) => role.name === roleName) || false
    },
    isAdmin: (state) => {
      return state.userProfile?.roles.some((role) => role.name === 'admin') || false
    },
  },

  actions: {
    // Initialize auth state from storage
    initializeAuth() {
      const accessToken = sessionStorage.getItem('access')
      const refreshToken = sessionStorage.getItem('refresh')
      const user = sessionStorage.getItem('user')

      if (accessToken && refreshToken && user) {
        try {
          this.accessToken = JSON.parse(accessToken)
          this.refreshToken = JSON.parse(refreshToken)
          this.user = JSON.parse(user)
          this.isAuthenticated = true

          // Update legacy fields for backward compatibility
          this.userName = this.user?.username || ''
          this.email = this.user?.email || ''

          // Auto-fetch user profile if authenticated
          if (this.isAuthenticated && !this.userProfile) {
            this.getUserProfile().catch((error) => {
              console.warn('Failed to auto-fetch user profile:', error)
            })
          }
        } catch (error) {
          console.error('Error parsing stored auth data:', error)
          this.clearAuth()
        }
      }
    },

    // Clear authentication state
    clearAuth() {
      this.user = null
      this.userProfile = null
      this.accessToken = null
      this.refreshToken = null
      this.isAuthenticated = false

      // Clear legacy fields
      this.userName = ''
      this.email = ''

      // Clear from storage
      sessionStorage.removeItem('access')
      sessionStorage.removeItem('refresh')
      sessionStorage.removeItem('user')
    },

    // Store authentication data
    setAuth(tokens: AuthTokens, user: AuthUser) {
      this.accessToken = tokens.access
      this.refreshToken = tokens.refresh
      this.user = user
      this.isAuthenticated = true

      // Update legacy fields for backward compatibility
      this.userName = user?.username || ''
      this.email = user?.email || ''

      // Store in session storage
      sessionStorage.setItem('access', JSON.stringify(tokens.access))
      sessionStorage.setItem('refresh', JSON.stringify(tokens.refresh))
      sessionStorage.setItem('user', JSON.stringify(user))
    },

    // Legacy methods for backward compatibility
    toggle2FA() {
      this.is2FAEnabled = !this.is2FAEnabled
    },

    changeUserName(userName: string) {
      this.userName = userName
    },

    // 1. User Registration
    async register(userData: RegisterPayload) {
      this.submitting = true
      try {
        const url = `${import.meta.env.VITE_APP_API_BASE_URL}${import.meta.env.VITE_APP_AUTH_REGISTER_URL}`

        const response = await makeRequest({
          url: url,
          method: 'post',
          data: userData,
        })

        if (response.status === 200 || response.status === 201) {
          this.submitting = false
          return response.data
        }
      } catch (error) {
        console.error('Registration failed:', error)
        this.submitting = false
        throw error
      }
    },

    // 2. Account Activation
    async activateAccount(activationData: ActivateAccountPayload) {
      this.submitting = true
      try {
        const url = `${import.meta.env.VITE_APP_API_BASE_URL}${import.meta.env.VITE_APP_AUTH_ACTIVATE_ACCOUNT_URL}`

        console.log('Activation API URL:', url) // Debug log
        console.log('Activation payload:', activationData) // Debug log

        const response = await makeRequest({
          url: url,
          method: 'post',
          data: activationData,
        })

        console.log('Activation response:', response) // Debug log

        // Handle successful response codes
        if (response.status === 200 || response.status === 201) {
          this.submitting = false
          return response.data
        } else {
          // Handle other status codes
          this.submitting = false
          console.warn('Unexpected response status:', response.status)
          return response.data || { message: 'Activation completed' }
        }
      } catch (error) {
        console.error('Account activation failed:', error)
        this.submitting = false
        throw error
      }
    },
    // 3. User Login (Updated - tokens only from login, user info from profile API)
    async login(loginData: LoginPayload | any) {
      this.submitting = true
      this.logingin = true // Legacy field

      try {
        const url = `${import.meta.env.VITE_APP_API_BASE_URL}${import.meta.env.VITE_APP_AUTH_LOGIN_URL}`

        // Handle both new and legacy payload formats
        const payload = {
          username: loginData.username,
          password: loginData.password,
        }

        const response = await makeRequest({
          url: url,
          method: 'post',
          data: payload,
        })

        if (response.status === 200) {
          const responseData = response.data

          // Extract tokens from login response (flexible token field names)
          const access = responseData.access || responseData.access_token || responseData.accessToken
          const refresh = responseData.refresh || responseData.refresh_token || responseData.refreshToken

          if (!access || !refresh) {
            throw new Error('Login response missing required tokens (access/refresh)')
          }

          // Store tokens temporarily
          this.accessToken = access
          this.refreshToken = refresh

          // Store tokens in session storage
          sessionStorage.setItem('access', JSON.stringify(access))
          sessionStorage.setItem('refresh', JSON.stringify(refresh))

          // Now fetch user profile using the profile API
          try {
            const userProfile = await this.getUserProfile()

            // Create user object from profile data
            const user: AuthUser = {
              id: userProfile.id,
              username: userProfile.username,
              email: userProfile.email,
            }

            // Set full authentication state
            this.user = user
            this.isAuthenticated = true

            // Update legacy fields for backward compatibility
            this.userName = user.username
            this.email = user.email

            // Store user in session storage
            sessionStorage.setItem('user', JSON.stringify(user))

            this.submitting = false
            this.logingin = false
            return { tokens: { access, refresh }, user }
          } catch (profileError) {
            console.error('Failed to fetch user profile after login:', profileError)
            // Clear tokens if profile fetch fails
            this.clearAuth()
            throw new Error('Login successful but failed to fetch user profile')
          }
        }
      } catch (error) {
        console.error('Login failed:', error)
        this.submitting = false
        this.logingin = false
        throw error
      }
    },

    // 4. Get User Profile
    async getUserProfile() {
      this.profileLoading = true
      try {
        const url = `${import.meta.env.VITE_APP_API_BASE_URL}${import.meta.env.VITE_APP_AUTH_USER_PROFILE_URL}`

        const response = await makeRequest({
          url: url,
          method: 'get',
        })

        if (response.status === 200) {
          this.userProfile = response.data
          this.profileLoading = false
          return response.data
        }
      } catch (error) {
        console.error('Failed to fetch user profile:', error)
        this.profileLoading = false
        throw error
      }
    },

    // 5. Update User Profile
    async updateUserProfile(profileData: UpdateProfilePayload) {
      this.submitting = true
      try {
        const url = `${import.meta.env.VITE_APP_API_BASE_URL}${import.meta.env.VITE_APP_AUTH_USER_PROFILE_URL}`

        const response = await makeRequest({
          url: url,
          method: 'patch',
          data: profileData,
        })

        if (response.status === 200) {
          // Update local profile data
          if (this.userProfile) {
            this.userProfile = { ...this.userProfile, ...response.data }
          }
          this.submitting = false
          return response.data
        }
      } catch (error) {
        console.error('Profile update failed:', error)
        this.submitting = false
        throw error
      }
    },

    // 6. Account Deactivation
    async deactivateAccount(deactivationData: DeactivateAccountPayload) {
      this.submitting = true
      try {
        const url = `${import.meta.env.VITE_APP_API_BASE_URL}${import.meta.env.VITE_APP_AUTH_DEACTIVATE_ACCOUNT_URL}`

        const response = await makeRequest({
          url: url,
          method: 'post',
          data: deactivationData,
        })

        console.log('Deactivation API response status:', response.status)
        console.log('Deactivation API response data:', response.data)

        if (response.status === 200) {
          // Clear auth state after deactivation
          this.clearAuth()
          this.submitting = false
          return response.data
        } else {
          // Return error response for non-200 status
          this.submitting = false
          return {
            status: 'error',
            message: response.data?.message || 'Deactivation failed',
            data: response.data,
          }
        }
      } catch (error) {
        console.error('Account deactivation failed:', error)
        this.submitting = false
        throw error
      }
    },

    // 7. Resend OTP
    async resendOtp(otpData: ResendOtpPayload) {
      this.submitting = true
      try {
        const url = `${import.meta.env.VITE_APP_API_BASE_URL}${import.meta.env.VITE_APP_AUTH_RESEND_OTP_URL}`

        const response = await makeRequest({
          url: url,
          method: 'post',
          data: otpData,
        })

        if (response.status === 200) {
          this.submitting = false
          return response.data
        }
      } catch (error) {
        console.error('Resend OTP failed:', error)
        this.submitting = false
        throw error
      }
    },

    // 8. Reactivate Account
    async reactivateAccount(reactivationData: ReactivateAccountPayload) {
      this.submitting = true
      try {
        const url = `${import.meta.env.VITE_APP_API_BASE_URL}${import.meta.env.VITE_APP_AUTH_REACTIVATE_ACCOUNT_URL}`

        const response = await makeRequest({
          url: url,
          method: 'post',
          data: reactivationData,
        })

        console.log('Reactivation API response status:', response.status)
        console.log('Reactivation API response data:', response.data)

        if (response.status === 200) {
          this.submitting = false
          return response.data
        } else {
          // Return error response for non-200 status
          this.submitting = false
          return {
            status: 'error',
            message: response.data?.message || 'Reactivation failed',
            data: response.data,
          }
        }
      } catch (error) {
        console.error('Account reactivation failed:', error)
        this.submitting = false
        throw error
      }
    },

    // 9. Reset Password Request
    async resetPassword(resetData: ResetPasswordPayload) {
      this.submitting = true
      try {
        const url = `${import.meta.env.VITE_APP_API_BASE_URL}${import.meta.env.VITE_APP_AUTH_RESET_PASSWORD_URL}`

        const response = await makeRequest({
          url: url,
          method: 'post',
          data: resetData,
        })

        if (response.status === 200) {
          this.submitting = false
          return response.data
        }
      } catch (error) {
        console.error('Password reset request failed:', error)
        this.submitting = false
        throw error
      }
    },

    // 10. Confirm Password Reset
    async confirmResetPassword(confirmData: ConfirmResetPasswordPayload) {
      this.submitting = true
      try {
        const url = `${import.meta.env.VITE_APP_API_BASE_URL}${
          import.meta.env.VITE_APP_AUTH_CONFIRM_RESET_PASSWORD_URL
        }`

        const response = await makeRequest({
          url: url,
          method: 'post',
          data: confirmData,
        })

        if (response.status === 200) {
          this.submitting = false
          return response.data
        }
      } catch (error) {
        console.error('Password reset confirmation failed:', error)
        this.submitting = false
        throw error
      }
    },

    // 11. Logout (Updated)
    async logout() {
      this.submitting = true
      try {
        const url = `${import.meta.env.VITE_APP_API_BASE_URL}${import.meta.env.VITE_APP_AUTH_LOGOUT_URL}`

        const logoutData: LogoutPayload = {
          refresh: this.refreshToken || '',
        }

        const response = await makeRequest({
          url: url,
          method: 'post',
          data: logoutData,
        })

        if (response.status === 200) {
          this.clearAuth()
          this.submitting = false
          return response.data
        }
      } catch (error) {
        console.error('Logout failed:', error)
        // Clear auth state even if logout request fails
        this.clearAuth()
        this.submitting = false
        throw error
      }
    },

    // Token refresh (for automatic token refresh)
    async refreshAccessToken() {
      try {
        const url = `${import.meta.env.VITE_APP_API_BASE_URL}${import.meta.env.VITE_APP_AUTH_REFRESH_TOKEN_URL}`

        const response = await makeRequest({
          url: url,
          method: 'post',
          data: {
            refresh: this.refreshToken,
          },
        })

        if (response.status === 200) {
          const { access } = response.data
          this.accessToken = access
          sessionStorage.setItem('access', JSON.stringify(access))
          return access
        }
      } catch (error) {
        console.error('Token refresh failed:', error)
        this.clearAuth()
        throw error
      }
    },
  },
})
